// hooks/useMarkerSync.js
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { useEffect, useRef, useCallback } from "react";

/**
 * @typedef {Object} MarkerEntry
 * @property {google.maps.marker.AdvancedMarkerElement} marker
 * @property {HTMLDivElement}                           element
 * @property {ItemGroup}                                group
 */

/**
 * Reconciles a JS Map of AdvancedMarkerElements against the grouped items.
 * - Adds markers for new group keys
 * - Updates group data for existing group keys (no marker recreation)
 * - Removes markers for group keys that no longer exist
 * Calls scheduleRender() after reconciliation.
 *
 * @param {Map<string, ItemGroup>}          groupedItems
 * @param {google.maps.Map|null}            map
 * @param {MarkerClusterer|null}            clusterer
 * @param {(groupKey: string) => void}      onMarkerClick
 * @returns {{ markerMapRef: React.MutableRefObject<Map<string, MarkerEntry>> }}
 */
export function useMarkerSync(
  groupedItems,
  map,
  clusterer,
  scheduleRender,
  onMarkerClick
) {
  const mapLibMarker = useMapsLibrary("marker");
  // Source of truth for all AdvancedMarker instances
  const markerMapRef = useRef(new Map());

  // Keep onMarkerClick fresh inside the effect without re-running it
  const onMarkerClickRef = useRef(onMarkerClick);
  useEffect(() => {
    onMarkerClickRef.current = onMarkerClick;
  }, [onMarkerClick]);

  useEffect(() => {
    if (!map || !clusterer) return;

    if (!mapLibMarker) return;

    const currentMap = markerMapRef.current;
    const incomingKeys = new Set(groupedItems.keys());
    const existingKeys = new Set(currentMap.keys());

    // Remove all the markers from the clusterer
    clusterer.clearMarkers(true);

    // --- Remove stale markers ---
    existingKeys.forEach((key) => {
      if (!incomingKeys.has(key)) {
        const entry = currentMap.get(key);
        entry.marker.setMap(null);
        entry.marker = null;
        currentMap.delete(key);
      }
    });

    // --- Add or update markers ---
    groupedItems.forEach((group, key) => {
      if (existingKeys.has(key)) {
        // Update existing entry in place — no marker recreation
        const entry = currentMap.get(key);
        entry.group = group;
        // const oldMapValue = entry.marker.map;
        // entry.marker.map = map;
        entry.marker.position = {
          lat: Number(group.lat),
          lng: Number(group.lng),
        };
        // entry.marker.map = oldMapValue;
        // clusterer.updateMarker(entry.marker);
        clusterer.addMarker(entry.marker, true);
      } else {
        // Create a new AdvancedMarker for this group
        const element = document.createElement("div");

        const marker = new mapLibMarker.AdvancedMarkerElement({
          position: {
            lat: Number(group.lat),
            lng: Number(group.lng),
          },
          content: element,
          map,
        });

        // Store count on the marker instance so the clusterer
        // renderer can access it for true cluster count calculation
        marker.__count = group.count;
        marker.__itemId = group.items[0].id;

        marker.addListener("gmb-click", () => {
          onMarkerClickRef.current(key);
        });

        currentMap.set(key, { marker, element, group });
        clusterer.addMarker(marker, true);
      }
    });

    // Update __count on all markers after reconciliation
    // (handles updates where count changes but marker already existed)
    currentMap.forEach((entry, key) => {
      entry.marker.__count = entry.group.count;
    });

    scheduleRender();
  }, [groupedItems, map, clusterer, mapLibMarker]);

  return { markerMapRef };
}
